import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from "@material-ui/core";
import api from "../../services/api";
import KoraxFormImportReviewDialog from "../KoraxFormImportReviewDialog";

const colors = {
  blue: "#0057FF",
  navy: "#010B36",
  text: "#243248",
  muted: "#64748B",
  border: "#E2E8F0",
  background: "#F7F9FD"
};

const formatValue = value => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "Não informado";
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map(item => String(item)).join(", ")
      : "Não informado";
  }

  if (typeof value === "boolean") {
    return value ? "Sim" : "Não";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const isTextAnswer = value =>
  (typeof value === "string" && value.trim().length > 0) ||
  (typeof value === "number" && Number.isFinite(value)) ||
  typeof value === "boolean" ||
  (Array.isArray(value) &&
    value.length > 0 &&
    value.length <= 30 &&
    value.every(item =>
      typeof item === "string" ||
      typeof item === "number"
    ));

const targets = {
  "contact.name": "Cliente · Nome",
  "contact.email": "Cliente · E-mail",
  "opportunity.title": "CRM · Título",
  "opportunity.product": "CRM · Produto",
  "opportunity.notes": "CRM · Observações",
  "contact.custom": "Cliente · Campo personalizado"
};

const boxStyle = {
  background: "#FFFFFF",
  border: `1px solid ${colors.border}`,
  borderRadius: 12,
  padding: 17,
  marginBottom: 14
};

const selectStyle = {
  width: "100%",
  background: "#FFFFFF",
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: "11px 10px",
  fontSize: 13,
  color: colors.text
};

const getError = error => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message;

  return typeof message === "string"
    ? message
    : "Não foi possível carregar a prévia.";
};

const suggestImportMappings = (preview, existing = {}) => {
  const next = { ...existing };
  const fields = preview?.fields || [];

  if (!preview?.contact || !Array.isArray(fields)) {
    return { mappings: next, skipped: 0 };
  }

  const normalize = value =>
    String(value || "").normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().replace(/\s+/g, " ").trim();

  const taken = new Set(
    Object.values(next).filter(
      target => target && target !== "contact.custom"
    )
  );

  const customNames = new Set(
    fields.filter(field =>
      next[field.key] === "contact.custom"
    ).map(field => normalize(field.label))
  );

  let skipped = 0;

  fields.forEach(field => {
    if (next[field?.key]) return;
    if (!field?.key || !isTextAnswer(field.value)) return;

    if (Object.keys(next).length >= 50) {
      skipped += 1;
      return;
    }

    const label = String(field.label || "").trim();
    const normalized = normalize(label);
    const value = formatValue(field.value).trim();

    if (!label || label.length > 120 ||
        !value || value.length > 255) {
      skipped += 1;
      return;
    }

    let target = "contact.custom";

    if (
      /^(nome|nome completo|nome do cliente)$/.test(normalized) &&
      !taken.has("contact.name")
    ) {
      target = "contact.name";
    } else if (
      field.type === "email" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) &&
      !taken.has("contact.email")
    ) {
      target = "contact.email";
    }

    if (target === "contact.custom") {
      if (customNames.has(normalized)) {
        skipped += 1;
        return;
      }
      customNames.add(normalized);
    } else {
      taken.add(target);
    }

    next[field.key] = target;
  });

  return { mappings: next, skipped };
};

export default function KoraxFormImportPreviewDialog({
  open,
  onBack,
  onClose,
  requestId,
  ticketId,
  onImported
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState({});
  const [mappingWarning, setMappingWarning] = useState("");

  const [selectedOpportunityId, setSelectedOpportunityId] =
    useState("");

  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [success, setSuccess] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open || !requestId || !ticketId) {
      return undefined;
    }

    let active = true;

    setLoading(true);
    setError("");
    setPreview(null);
    setMappings({});
    setSelectedOpportunityId("");
    setReviewOpen(false);
    setConfirmError("");
    setSuccess(false);

    api.get(
      `/korax-forms/requests/${encodeURIComponent(requestId)}/import-preview`
    )
      .then(response => {
        if (!active) return;

        const result = response.data;

        if (
          Number(result?.request?.id) !== Number(requestId) ||
          Number(result?.request?.ticketId) !== Number(ticketId) ||
          result?.previewOnly !== true ||
          !Array.isArray(result?.fields)
        ) {
          throw new Error("Resposta da prévia incompatível.");
        }

        setPreview(result);
      })
      .catch(err => {
        if (active) setError(getError(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, requestId, ticketId]);

  useEffect(() => {
    if (!preview?.importAllowed || !preview?.contact) return;

    const result = suggestImportMappings(preview);
    setMappings(result.mappings);
    setMappingWarning(
      result.skipped
        ? `${result.skipped} resposta(s) requerem revisão manual.`
        : ""
    );
  }, [preview]);

  const opportunities = Array.isArray(preview?.opportunities)
    ? preview.opportunities
    : [];

  const selectedOpportunity = opportunities.find(
    item => String(item.id) === String(selectedOpportunityId)
  );


  // Preserva as selecoes anteriores. Um destino nao pode
  // receber duas respostas diferentes.
  const handleMapping = (key, target) => {
    const conflict = target && target !== "contact.custom" &&
      Object.entries(mappings).find(
      ([otherKey, existingTarget]) =>
        otherKey !== String(key) && existingTarget === target
    );

    if (conflict) {
      setMappingWarning(
        "Este destino ja esta selecionado em outra resposta. " +
        "Escolha um destino diferente para manter ambas."
      );
      return;
    }

    setMappingWarning("");

    setMappings(previous => {
      const next = { ...previous };

      if (target) {
        next[key] = target;
      } else {
        delete next[key];
      }

      return next;
    });
  };

  // Seleciona somente correspondencias seguras, sem
  // associar CPF, CNPJ ou outros dados ao campo errado.
  const selectAllCompatible = () => {
    const result = suggestImportMappings(preview, mappings);
    setMappings(result.mappings);
    setMappingWarning(
      result.skipped
        ? `${result.skipped} resposta(s) sem destino automático.`
        : "Todos os campos compatíveis foram selecionados."
    );
  };

  const clearAllMappings = () => {
    setMappings({});
    setMappingWarning("");
  };

  const currentValue = (target, key) => {
    if (!preview) return null;

    if (target === "contact.custom") {
      const field = preview.fields?.find(item =>
        String(item.key) === String(key)
      );

      const normalize = value =>
        String(value || "").normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/\s+/g, " ")
          .trim();

      const matches = (preview.contact?.extraInfo || [])
        .filter(item =>
          normalize(item.name) === normalize(field?.label)
        );

      return matches.length === 1
        ? matches[0].value
        : null;
    }


    if (target === "contact.name") {
      return preview.contact?.name;
    }

    if (target === "contact.email") {
      return preview.contact?.email;
    }

    if (!selectedOpportunity) return null;

    if (target === "opportunity.title") {
      return selectedOpportunity.title;
    }

    if (target === "opportunity.product") {
      return selectedOpportunity.product;
    }

    if (target === "opportunity.notes") {
      return selectedOpportunity.notes;
    }

    return null;
  };

  const changes = useMemo(() => {
    if (!preview || !Array.isArray(preview.fields)) return [];

    return preview.fields
      .filter(field => {
        const target = mappings[field.key];

        if (!target || !isTextAnswer(field.value)) {
          return false;
        }

        if (target.startsWith("contact.")) {
          return Boolean(preview.contact);
        }

        return Boolean(selectedOpportunity);
      })
      .map(field => ({
        key: field.key,
        label: field.label,
        target: mappings[field.key],
        newValue: typeof field.value === "string"
          ? field.value.trim()
          : formatValue(field.value)
      }));
  }, [preview, mappings, selectedOpportunity]);

  const validTargets = field => {
    if (!isTextAnswer(field.value)) return [];

    const result = [];
    const label = String(field.label || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().trim();

    if (preview?.contact) {
      if (/^(nome|nome completo|nome do cliente)$/.test(label)) {
        result.push("contact.name");
      }

      if (
        field.type === "email" &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          String(field.value || "").trim()
        )
      ) {
        result.push("contact.email");
      }

      if (
        String(field.label || "").trim().length > 0 &&
        String(field.label || "").trim().length <= 120 &&
        formatValue(field.value).trim().length <= 255
      ) {
        result.push("contact.custom");
      }
    }

    if (selectedOpportunity) {
      result.push(
        "opportunity.title",
        "opportunity.product",
        "opportunity.notes"
      );
    }

    return result;
  };

  const openReview = () => {
    if (
      submittingRef.current ||
      success ||
      !preview?.importAllowed ||
      preview?.submission?.importedAt ||
      !changes.length
    ) return;

    setConfirmError("");
    setReviewOpen(true);
  };

  const confirmSelectedChanges = async () => {
    if (
      submittingRef.current ||
      !reviewOpen ||
      success ||
      !preview?.importAllowed ||
      !changes.length
    ) return;

    const includesOpportunity = changes.some(
      change => change.target.startsWith("opportunity.")
    );

    if (
      includesOpportunity &&
      !Number.isSafeInteger(Number(selectedOpportunityId))
    ) return;

    const payload = {
      changes: changes.map(change => {
        const before = currentValue(change.target, change.key);

        return {
          key: change.key,
          target: change.target,
          expectedCurrentValue:
            before === null || before === undefined
              ? null
              : String(before)
        };
      }),
      ...(includesOpportunity ? {
        opportunityId: Number(selectedOpportunityId)
      } : {})
    };

    submittingRef.current = true;
    setSubmitting(true);
    setConfirmError("");

    try {
      const response = await api.post(
        `/korax-forms/requests/${encodeURIComponent(requestId)}/import-confirm`,
        payload
      );

      const result = response.data;

      if (
        result?.success !== true ||
        Number(result?.requestId) !== Number(requestId)
      ) {
        throw new Error("Resposta de confirmação inesperada.");
      }

      setPreview(previous => previous ? ({
        ...previous,
        importAllowed: false,
        submission: {
          ...previous.submission,
          importedAt: result.importedAt
        }
      }) : previous);

      setSuccess(true);
      setReviewOpen(false);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Não foi possível concluir a importação. Confira os dados e tente novamente.";

      setConfirmError(String(message));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (submittingRef.current) return;

    if (success && typeof onImported === "function") {
      onImported();
      return;
    }

    onBack();
  };

  const handleClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="korax-import-preview-title"
      PaperProps={{
        style: {
          borderRadius: 16
        }
      }}
    >
      <DialogTitle id="korax-import-preview-title">
        <div style={{
          fontSize: 19,
          fontWeight: 800,
          color: colors.navy
        }}>
          Preparar importação
        </div>

        <div style={{
          color: colors.muted,
          fontSize: 12,
          marginTop: 6
        }}>
          Selecione quais respostas poderão atualizar
          o cadastro do cliente ou uma oportunidade.
        </div>
      </DialogTitle>

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "12px 24px",
          background: "#FFFFFF",
          borderBottom: "1px solid #E5EAF2"
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#334155"
          }}>
            {Object.values(mappings).filter(Boolean).length}
            {" campo(s) selecionado(s)"}
          </div>

          <div style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap"
          }}>
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={selectAllCompatible}
              disabled={
                !preview?.importAllowed ||
                !Array.isArray(preview?.fields)
              }
              style={{
                textTransform: "none",
                fontWeight: 750,
                background: "#0057FF"
              }}
            >
              Selecionar campos compatíveis
            </Button>

            <Button
              type="button"
              variant="outlined"
              onClick={clearAllMappings}
              disabled={!Object.keys(mappings).length}
              style={{
                textTransform: "none",
                fontWeight: 700
              }}
            >
              Limpar seleção
            </Button>
          </div>
        </div>

        {mappingWarning && (
          <div role="status" style={{
            padding: "10px 24px",
            background: "#FFF7E8",
            color: "#895B12",
            fontSize: 12,
            lineHeight: 1.5
          }}>
            {mappingWarning}
          </div>
        )}


      <DialogContent
        dividers
        style={{
          background: colors.background,
          maxHeight: "73vh",
          minHeight: 230
        }}
      >
        {success && (
          <div role="status" style={{
            ...boxStyle,
            border: "1px solid #86D5AD",
            background: "#F0FDF4"
          }}>
            <strong style={{ color: "#166534" }}>
              Importação realizada com sucesso.
            </strong>
            <p style={{
              fontSize: 12,
              marginBottom: 0,
              color: "#166534"
            }}>
              Os campos selecionados foram atualizados.
              A operação foi registrada no histórico.
            </p>
          </div>
        )}

        {loading && (
          <div style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: 24,
            color: colors.muted
          }}>
            <CircularProgress size={20} />
            Carregando informações...
          </div>
        )}

        {error && (
          <div role="alert" style={{
            ...boxStyle,
            background: "#FFF1F2",
            color: "#A52C45",
            fontSize: 13
          }}>
            {error}
          </div>
        )}

        {preview && !success && (
          <>
            <div style={boxStyle}>
              <div style={{
                fontSize: 11,
                fontWeight: 800,
                color: colors.blue,
                textTransform: "uppercase",
                marginBottom: 8
              }}>
                Origem das informações
              </div>

              <div style={{
                fontWeight: 800,
                fontSize: 16,
                color: colors.navy
              }}>
                {preview.request?.title || "Formulário"}
              </div>

              <div style={{
                marginTop: 8,
                fontSize: 12,
                color: colors.muted
              }}>
                Atendimento #{preview.request?.ticketId}
              </div>
            </div>

            <div style={boxStyle}>
              <div style={{
                fontSize: 14,
                fontWeight: 800,
                marginBottom: 12,
                color: colors.navy
              }}>
                Destinos disponíveis
              </div>

              {preview.contact ? (
                <div style={{
                  fontSize: 13,
                  lineHeight: 1.7,
                  marginBottom: 14
                }}>
                  <strong>Cliente:</strong>{" "}
                  {preview.contact.name || "Sem nome"}

                  <div style={{
                    fontSize: 12,
                    color: colors.muted,
                    overflowWrap: "anywhere"
                  }}>
                    {preview.contact.email || "Sem e-mail"}
                  </div>
                </div>
              ) : (
                <p style={{
                  color: colors.muted,
                  fontSize: 12
                }}>
                  Nenhum cliente disponível para importação.
                </p>
              )}

              <label style={{
                display: "block",
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 7
              }}>
                Oportunidade do CRM
              </label>

              <select
                value={selectedOpportunityId}
                onChange={event => {
                  setSelectedOpportunityId(event.target.value);

                  setMappings(previous => {
                    const next = { ...previous };

                    Object.keys(next).forEach(key => {
                      if (next[key]?.startsWith("opportunity.")) {
                        delete next[key];
                      }
                    });

                    return next;
                  });
                }}
                style={selectStyle}
                disabled={!opportunities.length}
              >
                <option value="">
                  {opportunities.length
                    ? "Selecione uma oportunidade"
                    : "Nenhuma oportunidade vinculada disponível"}
                </option>

                {opportunities.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.title || `Oportunidade #${item.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div style={boxStyle}>
              <h3 style={{
                fontSize: 15,
                color: colors.navy,
                margin: "0 0 8px"
              }}>
                Selecionar respostas
              </h3>

              <p style={{
                color: colors.muted,
                fontSize: 12,
                margin: "0 0 20px",
                lineHeight: 1.6
              }}>
                Escolha o destino de cada resposta.
                Campos não selecionados serão ignorados.
              </p>

              {preview.fields.map((field, index) => (
                <div
                  key={`${field.key}-${index}`}
                  style={{
                    paddingBottom: 17,
                    marginBottom: 17,
                    borderBottom: `1px solid ${colors.border}`
                  }}
                >
                  <div style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: colors.text,
                    overflowWrap: "anywhere"
                  }}>
                    {index + 1}. {field.label}
                  </div>

                  <div style={{
                    background: colors.background,
                    padding: 11,
                    borderRadius: 8,
                    fontSize: 13,
                    color: colors.text,
                    margin: "9px 0 12px",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere"
                  }}>
                    {formatValue(field.value)}
                  </div>

                  <label style={{
                    fontSize: 11,
                    display: "block",
                    color: colors.muted,
                    marginBottom: 7
                  }}>
                    Importar para
                  </label>

                  <select
                    value={mappings[field.key] || ""}
                    onChange={event =>
                      handleMapping(field.key, event.target.value)
                    }
                    disabled={!validTargets(field).length}
                    style={selectStyle}
                  >
                    <option value="">
                      {validTargets(field).length
                        ? "Não importar"
                        : "Nenhum destino compatível"}
                    </option>

                    {validTargets(field).map(target => (
                      <option key={target} value={target}>
                        {target === "contact.custom"
                          ? `Cliente · ${field.label || "Campo personalizado"}`
                          : targets[target]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div style={boxStyle}>
              <h3 style={{
                color: colors.navy,
                fontSize: 15,
                margin: "0 0 8px"
              }}>
                Prévia das alterações
              </h3>

              <p style={{
                color: colors.muted,
                fontSize: 12,
                lineHeight: 1.6
              }}>
                Confira o valor atual e o valor sugerido.
                Esta tela não salva nenhuma alteração.
              </p>

              {!changes.length && (
                <div style={{
                  color: colors.muted,
                  fontSize: 13,
                  padding: "18px 0"
                }}>
                  Selecione pelo menos uma resposta acima
                  para montar a prévia.
                </div>
              )}

              {changes.map(change => (
                <div
                  key={change.key}
                  style={{
                    borderTop: `1px solid ${colors.border}`,
                    padding: "16px 0"
                  }}
                >
                  <div style={{
                    color: colors.blue,
                    fontWeight: 800,
                    fontSize: 12,
                    marginBottom: 10
                  }}>
                    {targets[change.target]}
                  </div>

                  <div style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap"
                  }}>
                    <div style={{
                      flex: 1,
                      minWidth: 170,
                      fontSize: 12
                    }}>
                      <div style={{
                        color: colors.muted,
                        marginBottom: 6
                      }}>
                        Valor atual
                      </div>

                      <div style={{
                        padding: 11,
                        background: colors.background,
                        borderRadius: 8,
                        overflowWrap: "anywhere",
                        whiteSpace: "pre-wrap"
                      }}>
                        {formatValue(currentValue(change.target, change.key))}
                      </div>
                    </div>

                    <div style={{
                      flex: 1,
                      minWidth: 170,
                      fontSize: 12
                    }}>
                      <div style={{
                        color: colors.muted,
                        marginBottom: 6
                      }}>
                        Novo valor
                      </div>

                      <div style={{
                        padding: 11,
                        background: "#EAF0FF",
                        borderRadius: 8,
                        overflowWrap: "anywhere",
                        whiteSpace: "pre-wrap"
                      }}>
                        {change.newValue}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              fontSize: 12,
              color: colors.muted,
              lineHeight: 1.7,
              padding: "3px 8px 15px"
            }}>
              Prévia somente de leitura. A efetivação da
              importação será liberada em uma próxima etapa,
              após a validação do backend.
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions style={{
        padding: "15px 20px",
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8
      }}>
        <Button
          onClick={handleBack}
          disabled={submitting}
          style={{
            textTransform: "none",
            color: colors.blue,
            fontWeight: 700
          }}
        >
          Voltar às respostas
        </Button>

        <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap"
        }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            style={{ textTransform: "none" }}
          >
            Fechar
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={openReview}
            disabled={
              loading ||
              submitting ||
              success ||
              !preview?.importAllowed ||
              Boolean(preview?.submission?.importedAt) ||
              changes.length === 0
            }
            style={{
              textTransform: "none",
              fontWeight: 800
            }}
          >
            {preview?.submission?.importedAt
              ? "Já importado"
              : success
                ? "Importação concluída"
                : "Revisar e confirmar"}
          </Button>
        </div>
      </DialogActions>

      <KoraxFormImportReviewDialog
        open={reviewOpen && !success}
        rows={changes.map(change => ({
          ...change,
          title: targets[change.target],
          before: currentValue(change.target, change.key)
        }))}
        submitting={submitting}
        error={confirmError}
        onCancel={() => {
          if (!submittingRef.current) setReviewOpen(false);
        }}
        onConfirm={confirmSelectedChanges}
      />
    </Dialog>
  );
}
